import { keepPreviousData } from '@tanstack/react-query';
import { useStanQuery } from '@/queries';
import { QUERY_KEYS, SENTRY_FEATURES } from '@/lib';
import {
    fetchAutomation,
    fetchAutomationAnalytics,
    fetchAutomations,
    fetchInstagramBusinessAccounts,
    fetchInstagramPosts,
    fetchWeeklyInstagramPosts,
} from '@/lib/api/automations';

/**
 * Fetch all automations
 */
export function useAutomations() {
    return useStanQuery({
        queryKey: [QUERY_KEYS.AUTOMATIONS],
        queryFn: fetchAutomations,
        staleTime: 1000 * 60 * 5, // 5 minutes
        placeholderData: keepPreviousData,
        meta: {
            error: {
                sentryMessage: 'Failed to fetch automations',
                title: 'Error Loading Automations',
                message: 'Unable to load automations. Please try again.',
                feature: SENTRY_FEATURES.AUTOMATIONS,
            },
        },
    });
}

/**
 * Fetch single automation by ID
 */
export function useAutomation(automationId: number) {
    return useStanQuery({
        queryKey: [QUERY_KEYS.AUTOMATION_DETAILS, automationId],
        queryFn: () => fetchAutomation(automationId),
        enabled: automationId > 0,
        staleTime: 1000 * 60 * 5, // 5 minutes
        meta: {
            error: {
                sentryMessage: 'Failed to fetch automation details',
                title: 'Error Loading Automation',
                message: 'Unable to load automation details. Please try again.',
                feature: SENTRY_FEATURES.AUTOMATIONS,
            },
        },
    });
}

/**
 * Fetch automation analytics
 * @param automationIds - Array of automation IDs to fetch analytics for
 */
export function useAutomationAnalytics(automationIds: number[]) {
    return useStanQuery({
        queryKey: [QUERY_KEYS.AUTOMATION_ANALYTICS, automationIds],
        queryFn: () => fetchAutomationAnalytics(automationIds),
        enabled: automationIds.length > 0,
        staleTime: 1000 * 60 * 5, // 5 minutes
        placeholderData: keepPreviousData, // Keep previous data while refetching to prevent loading states
        meta: {
            error: {
                sentryMessage: 'Failed to fetch automation analytics',
                title: 'Error Loading Analytics',
                message: 'Unable to load automation analytics. Please try again.',
                feature: SENTRY_FEATURES.AUTOMATIONS,
            },
        },
    });
}

/**
 * Fetch Instagram posts for post picker
 * @param isInstagramConnected - Only fetches posts when Instagram is connected
 */
export function useInstagramPosts(isInstagramConnected = true) {
    return useStanQuery({
        queryKey: [QUERY_KEYS.INSTAGRAM_POSTS],
        queryFn: fetchInstagramPosts,
        enabled: isInstagramConnected,
        staleTime: 1000 * 60 * 10, // 10 minutes
        meta: {
            error: {
                sentryMessage: 'Failed to fetch Instagram posts',
                title: 'Error Loading Posts',
                message: 'Unable to load Instagram posts. Please try again.',
                feature: SENTRY_FEATURES.AUTOMATIONS,
            },
        },
    });
}

/**
 * Fetch Instagram business accounts
 */
export function useInstagramBusinessAccounts() {
    return useStanQuery({
        queryKey: [QUERY_KEYS.INSTAGRAM_BUSINESS_ACCOUNTS],
        queryFn: fetchInstagramBusinessAccounts,
        staleTime: 1000 * 60 * 30, // 30 minutes
        meta: {
            error: {
                sentryMessage: 'Failed to fetch Instagram business accounts',
                title: 'Error Loading Accounts',
                message: 'Unable to load Instagram accounts. Please try again.',
                feature: SENTRY_FEATURES.AUTOMATIONS,
            },
        },
    });
}

/**
 * Fetch weekly Instagram post count
 */
export function useWeeklyInstagramPosts() {
    return useStanQuery({
        queryKey: [QUERY_KEYS.WEEKLY_INSTAGRAM_POSTS],
        queryFn: fetchWeeklyInstagramPosts,
        staleTime: 1000 * 60 * 60, // 1 hour
        meta: {
            error: {
                sentryMessage: 'Failed to fetch weekly Instagram posts',
                title: 'Error Loading Post Count',
                message: 'Unable to load weekly post count. Please try again.',
                feature: SENTRY_FEATURES.AUTOMATIONS,
            },
        },
    });
}
